import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Calendar, Users, ArrowLeft, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

const ArticlePage = () => {
  const { t } = useTranslation();
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Get article data from localStorage or fetch from API
    const storedArticle = localStorage.getItem('currentArticle');
    if (storedArticle) {
      setArticle(JSON.parse(storedArticle));
    } else {
      // If no article found, redirect to news page
      navigate('/news');
    }
  }, [articleId, navigate]);

  const getCategoryColor = (category) => {
    const colors = {
      Mining: 'bg-blue-100 text-blue-900',
      Infrastructure: 'bg-green-100 text-green-900',
      Energy: 'bg-yellow-100 text-yellow-900',
      Technology: 'bg-purple-100 text-purple-900',
      Agriculture: 'bg-orange-100 text-orange-900',
      Economy: 'bg-red-100 text-red-900',
      Leadership: 'bg-indigo-100 text-indigo-900',
      'Business Expansion': 'bg-teal-100 text-teal-900',
      'Real Estate': 'bg-pink-100 text-pink-900',
      Investment: 'bg-cyan-100 text-cyan-900',
      'International Trade': 'bg-violet-100 text-violet-900'
    };
    return colors[category] || 'bg-gray-100 text-gray-900';
  };

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-900 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Back Button */}
      <motion.div 
        className="fixed top-24 left-4 z-50"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link to="/news">
          <Button 
            variant="outline" 
            className="bg-white border-2 border-blue-900 text-blue-900 hover:bg-blue-50 shadow-lg"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.backToNews')}
          </Button>
        </Link>
      </motion.div>

      {/* Article Header Image */}
      <motion.div 
        className="relative h-96 md:h-[500px] overflow-hidden bg-gradient-to-br from-blue-100 to-blue-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.style.background = 'linear-gradient(to bottom right, #dbeafe, #eff6ff)';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        
        {/* Article Meta on Image */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 text-white">
          <div className="max-w-5xl mx-auto">
            <Badge className={`${getCategoryColor(article.category)} mb-4`}>
              {article.category}
            </Badge>
            <motion.h1 
              className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {article.title}
            </motion.h1>
            <motion.div 
              className="flex flex-wrap items-center gap-6 text-blue-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                <span className="text-lg">{article.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span className="text-lg">{article.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span className="text-lg">{article.readTime}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Article Content */}
      <motion.article 
        className="py-16 px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <div className="max-w-4xl mx-auto">
          {/* Excerpt */}
          <Card className="p-8 mb-12 border-l-4 border-blue-900 bg-blue-50">
            <p className="text-xl text-gray-800 leading-relaxed italic">
              {article.excerpt}
            </p>
          </Card>

          {/* Full Content */}
          <div className="prose prose-lg max-w-none">
            {article.fullContent.split('\n\n').map((paragraph, index) => (
              <motion.p 
                key={index} 
                className="text-gray-700 leading-relaxed mb-6 text-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
              >
                {paragraph}
              </motion.p>
            ))}
          </div>

          {/* Share/Back Section */}
          <div className="mt-16 pt-8 border-t-2 border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-2">{t('news.writtenBy')}</p>
                <p className="text-xl font-bold text-blue-900">{article.author}</p>
              </div>
              <Link to="/news">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white px-8 py-6 text-lg group"
                >
                  <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
                  {t('news.backToAllNews')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </motion.article>

      {/* Related Articles Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('news.continueReading')}</h2>
          <div className="text-center">
            <Link to="/news">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-blue-900 text-blue-900 hover:bg-blue-50 px-8 py-6 text-lg"
              >
                {t('news.viewAllArticles')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticlePage;
